import {
  DEFAULT_FOLDER_ID,
  FOLDER_TRASH_ID,
  isVirtualFolder,
  parseFolderId,
} from "@zapier/utils";
import {
  isZapOwnerPlus,
  resolveZapCanDelete,
  ZAP_FOLDER_PERMISSION_LEVELS,
} from "./resolveZapCanDelete";

export interface ZapDeleteMetadata {
  id: string;
  customuserId?: number;
  folderId?: string | null;
  deletedAt?: string | null;
}

interface ZapLike {
  id: string | number;
  customuserId?: number | null;
  /** Trash index rows carry the owner here instead of `customuserId`. */
  ownerId?: number | null;
  folderId?: string | number | null;
  deletedAt?: string | null;
}

/** Picks the fields the delete rule needs from a Zap or hydrated Zap asset. */
export function toZapDeleteMetadata(zap: ZapLike): ZapDeleteMetadata {
  return {
    id: String(zap.id),
    customuserId: zap.customuserId ?? zap.ownerId ?? undefined,
    folderId: zap.folderId == null ? null : String(zap.folderId),
    deletedAt: zap.deletedAt ?? null,
  };
}

interface FolderPermissionsLookup {
  getFolderCanDelete: (folderId: string) => Promise<boolean | undefined>;
  getFolderIsShared: (folderId: string) => Promise<boolean>;
}

interface GetDeletableZapIdsInput extends FolderPermissionsLookup {
  zaps: ZapDeleteMetadata[];
  customuserId: number | undefined;
  isAccountOwner: boolean;
  isSuperAdmin: boolean;
  isMPFEnabled: boolean;
}

interface FolderPermissionsRequest {
  assetIds: string[];
  assetType: "folder";
  permissionLevels: string[];
}

/** Same request the Zap kebab makes, so the lookup resolves from its cache. */
export function buildZapFolderPermissionsRequest(
  folderId: string,
): FolderPermissionsRequest {
  return {
    assetIds: [folderId],
    assetType: "folder",
    permissionLevels: ZAP_FOLDER_PERMISSION_LEVELS,
  };
}

function isTrashedZap(zap: ZapDeleteMetadata): boolean {
  return !!zap.deletedAt || parseFolderId(zap.folderId) === FOLDER_TRASH_ID;
}

function isFolderLookupNeeded(
  folderId: string,
  isMPFEnabled: boolean,
): boolean {
  if (isVirtualFolder(folderId) && folderId !== DEFAULT_FOLDER_ID) {
    return false;
  }
  // Without MPF the kebab reads sharing from the folder tree, home folder included.
  return !isMPFEnabled || folderId !== DEFAULT_FOLDER_ID;
}

/**
 * Returns the ids of the Zaps the user can move to trash, applying the same
 * rule as the Zap kebab menu. Looks up each distinct folder once, never each
 * Zap. Zaps already in trash can only be purged by their owner, the account
 * owner or a super admin, matching the purge endpoint.
 */
export async function getDeletableZapIds({
  zaps,
  customuserId,
  isAccountOwner,
  isSuperAdmin,
  isMPFEnabled,
  getFolderCanDelete,
  getFolderIsShared,
}: GetDeletableZapIdsInput): Promise<string[]> {
  const isOwnerPlus = (zap: ZapDeleteMetadata) =>
    isZapOwnerPlus({
      customuserId,
      zapOwnerId: zap.customuserId,
      isAccountOwner,
      isSuperAdmin,
    });

  const zapsToCheck = zaps.filter((zap) => !isTrashedZap(zap));
  const purgeableZapIds = zaps
    .filter((zap) => isTrashedZap(zap) && isOwnerPlus(zap))
    .map((zap) => zap.id);

  const zapsWithOwnership = zapsToCheck.map((zap) => ({
    zap,
    folderId: parseFolderId(zap.folderId),
    isOwnerPlus: isOwnerPlus(zap),
  }));

  const foldersToLookUp = [
    ...new Set(
      zapsWithOwnership
        .filter(
          ({ isOwnerPlus, folderId }) =>
            !isOwnerPlus && isFolderLookupNeeded(folderId, isMPFEnabled),
        )
        .map(({ folderId }) => folderId),
    ),
  ];

  const folderLookupEntries = await Promise.all(
    foldersToLookUp.map(async (folderId) => {
      const [canDelete, isShared] = await Promise.all([
        isMPFEnabled ? getFolderCanDelete(folderId) : undefined,
        isMPFEnabled ? false : getFolderIsShared(folderId),
      ]);
      return [folderId, { canDelete, isShared }] as const;
    }),
  );
  const folderLookup = new Map(folderLookupEntries);

  const deletableZapIds = zapsWithOwnership
    .filter(({ zap, folderId, isOwnerPlus }) => {
      const lookup = folderLookup.get(folderId);
      return resolveZapCanDelete({
        folderId,
        isDeleted: !!zap.deletedAt,
        isMPFEnabled,
        isOwnerPlus,
        folderIsShared: lookup?.isShared ?? false,
        folderCanDelete:
          folderId === DEFAULT_FOLDER_ID ? true : lookup?.canDelete,
      });
    })
    .map(({ zap }) => zap.id);

  return [...deletableZapIds, ...purgeableZapIds];
}
